/**
 * Product and vendor facts for the buyer coach: shelf life, storage space, freight.
 *
 * Greg, 2026-10-09: the coach kept every conversation and learned from none of them.
 * These are the things that decide an order size and are in nobody's database, so they
 * are told to us once, saved with the name of whoever said so, and handed to the coach
 * on every order from then on.
 *
 * Three places show them, all built from the pieces in this file so there is one way a
 * fact looks and one way it is added:
 *   - PlanFacts    under a Plan an Order chat (and where the coach's proposals wait)
 *   - VendorFacts  on a vendor's card
 *   - FactsList    every fact, on Accounting > Vendors
 *
 * ⚠️ THE COACH CAN ONLY PROPOSE. A proposal is not a fact until a person presses Save
 * here, and that person's name goes on it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { apBuyFacts } from '../services/api';

const KIND_ORDER = ['usage', 'shelf_life', 'storage', 'freight', 'lead_time', 'min_order', 'terms', 'rep', 'other'];
const KIND_LABELS = {
  usage: 'How much we use', shelf_life: 'Shelf life', storage: 'Storage space', freight: 'Freight',
  lead_time: 'Lead time', min_order: 'Minimum order', terms: 'Payment terms', rep: 'Sales rep', other: 'Other',
};
// The ones that are about the vendor whatever is being bought, so the vendor tick starts on.
const VENDOR_KINDS = ['freight', 'lead_time', 'min_order', 'terms', 'rep'];
const HINTS = {
  usage: 'e.g. One bag per dozen clams; a 50 count takes 4 and a 100 count takes 8',
  shelf_life: 'e.g. Keeps 10 days refrigerated, 12 months frozen',
  storage: 'e.g. We have room for 40 cases in the walk-in',
  freight: 'e.g. $75 minimum freight charge; free freight over $1,500',
  lead_time: 'e.g. Arrives about a week after ordering',
  min_order: 'e.g. Sold by the case of 2,000; $150 minimum order',
  terms: 'e.g. Net 30',
  rep: 'e.g. Dana Smith, 410-555-0142, dana@lifoam.com',
  other: 'e.g. Two-week lead time; sold by the pallet only',
};
const fmtDay = (t) => {
  const d = new Date(t);
  return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const chip = {
  display: 'inline-block', fontSize: 10.5, fontWeight: 700, borderRadius: 10, padding: '1px 7px',
  background: '#eef4fa', color: 'var(--navy, #00395C)', whiteSpace: 'nowrap', marginRight: 6,
};

function FactRow({ f, onRetire, busy, showVendor = true }) {
  const about = [f.item, showVendor && f.vendor ? f.vendor : null].filter(Boolean).join(' · ');
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', fontSize: 13 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={chip}>{f.kindLabel}</span>
        {about && <strong>{about}: </strong>}{f.fact}
        <span style={{ fontSize: 11.5, color: 'var(--gray-500, #6b7280)' }}>
          {' '}{f.by ? f.by.split(' ')[0] : ''}{f.at ? `, ${fmtDay(f.at)}` : ''}
        </span>
      </div>
      {onRetire && (
        <button className="btn btn-ghost" style={{ padding: 0, whiteSpace: 'nowrap' }} disabled={busy}
          title="No longer true" onClick={() => onRetire(f)}>Remove</button>
      )}
    </div>
  );
}

// The one form a fact is added through. `vendorName` fixes the vendor (a vendor card);
// `offerVendor` offers it as a tick (a plan, where the fact may be about the product).
function AddFact({ vendorName, offerVendor, planId, onSaved, defaultKind = 'shelf_life' }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(defaultKind);
  const [item, setItem] = useState('');
  const [fact, setFact] = useState('');
  const [useVendor, setUseVendor] = useState(VENDOR_KINDS.includes(defaultKind));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const pickKind = (k) => { setKind(k); if (offerVendor) setUseVendor(VENDOR_KINDS.includes(k)); };
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const vendor = vendorName || (offerVendor && useVendor ? offerVendor : '');
      const out = await apBuyFacts('add', { kind, item, vendor, fact, planId });
      setItem(''); setFact(''); setOpen(false);
      if (onSaved) onSaved(out);
    } catch (e) { setErr(e.message || 'That did not save.'); }
    setBusy(false);
  };

  if (!open) {
    return (
      <button className="btn btn-ghost" style={{ padding: 0 }} onClick={() => setOpen(true)}>
        <Plus size={13} /> Add A Fact
      </button>
    );
  }
  return (
    <div style={{ border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 6, padding: 10, marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {KIND_ORDER.map((k) => (
          <button key={k} className={kind === k ? 'btn btn-navy' : 'btn btn-outline'} style={{ fontSize: 12 }}
            onClick={() => pickKind(k)}>{KIND_LABELS[k]}</button>
        ))}
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        <input className="form-input" value={item} onChange={(e) => setItem(e.target.value)}
          placeholder={vendorName || (offerVendor && useVendor)
            ? 'Which product (leave empty if it applies to everything from this vendor)'
            : 'Which product, e.g. fresh jumbo lump crab meat'}
          style={{ width: '100%' }} />
        <input className="form-input" value={fact} onChange={(e) => setFact(e.target.value)}
          placeholder={HINTS[kind]} style={{ width: '100%' }}
          onKeyDown={(e) => { if (e.key === 'Enter' && fact.trim()) save(); }} />
        {offerVendor && !vendorName && (
          <label style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={useVendor} onChange={(e) => setUseVendor(e.target.checked)} />
            This is about {offerVendor} (it goes on their vendor card)
          </label>
        )}
      </div>
      {err && <div style={{ color: '#b42318', fontSize: 12.5, marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !fact.trim()} onClick={save}>
          {busy ? 'Saving…' : 'Save Fact'}
        </button>
        <button className="btn btn-outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

// Under a Plan an Order chat.
export function PlanFacts({ planId, vendorName, reloadKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try { setData(await apBuyFacts('plan', { planId })); setErr(''); }
    catch (e) { setErr(e.message); }
  }, [planId]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const act = async (action, id) => {
    setBusy(true); setErr('');
    try { setData(await apBuyFacts(action, { id, planId })); }
    catch (e) { setErr(e.message || 'That did not save.'); }
    setBusy(false);
  };

  if (!data) return null;
  const { pending = [], relevant = [] } = data;
  return (
    <div style={{
      border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 8, padding: '10px 12px', margin: '0 0 10px',
      background: '#fff',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--gray-500, #6b7280)', marginBottom: 4 }}>
        What we know for this order
      </div>

      {pending.map((f) => (
        <div key={f.id} style={{
          background: '#fff7ed', border: '1px solid #f6d4b8', borderRadius: 6, padding: '8px 10px', margin: '6px 0',
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 260px', fontSize: 13 }}>
            <strong>Save this for next time?</strong>{' '}
            <span style={chip}>{f.kindLabel}</span>
            {[f.item, f.vendor].filter(Boolean).join(' · ')}: {f.fact}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-primary" style={{ fontSize: 12 }} disabled={busy} onClick={() => act('confirm', f.id)}>
              <Check size={13} /> Save
            </button>
            <button className="btn btn-outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => act('retire', f.id)}>
              <X size={13} /> No
            </button>
          </div>
        </div>
      ))}

      {relevant.length === 0 && pending.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--gray-600, #4b5563)', marginBottom: 4 }}>
          Nothing saved yet for this vendor or product. How much of it each sale uses, shelf life,
          how much we have room for and the freight terms all help the coach size the order. Add them
          here, or just tell the coach.
        </div>
      )}
      {relevant.map((f) => <FactRow key={f.id} f={f} busy={busy} onRetire={(x) => act('retire', x.id)} />)}

      {err && <div style={{ color: '#b42318', fontSize: 12.5 }}>{err}</div>}
      <div style={{ marginTop: 4 }}>
        <AddFact offerVendor={vendorName} planId={planId} onSaved={(out) => setData((d) => ({ ...d, ...out }))} />
      </div>
    </div>
  );
}

// On a vendor's card. `facts` comes with the vendor; a change asks the card to re-read it.
export function VendorFacts({ vendorName, facts = [], onChanged }) {
  const [busy, setBusy] = useState(false);
  const retire = async (f) => {
    setBusy(true);
    try { await apBuyFacts('retire', { id: f.id }); if (onChanged) await onChanged(); } catch (e) { /* the card re-reads */ }
    setBusy(false);
  };
  return (
    <div>
      {facts.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--gray-500, #6b7280)' }}>
          Nothing saved. Freight minimums, lead times and anything else worth knowing before ordering go here.
        </div>
      )}
      {facts.map((f) => <FactRow key={f.id} f={f} busy={busy} showVendor={false} onRetire={retire} />)}
      <AddFact vendorName={vendorName} defaultKind="freight" onSaved={() => onChanged && onChanged()} />
    </div>
  );
}

// Every fact, for looking over and tidying.
export function FactsList() {
  const [facts, setFacts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { setFacts((await apBuyFacts('list')).facts || []); } catch (e) { setErr(e.message); setFacts([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const retire = async (f) => {
    setBusy(true);
    try { setFacts((await apBuyFacts('retire', { id: f.id })).facts || []); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  if (!facts) return <div style={{ color: 'var(--gray-500, #6b7280)' }}>Loading the facts…</div>;
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontSize: 13.5, color: 'var(--gray-600, #4b5563)', marginBottom: 10, maxWidth: 760 }}>
        What the team has told the buyer coach about products and vendors. The coach reads every
        one of these on every order. Remove one when it stops being true and add the new one.
      </div>
      {err && <div style={{ color: '#b42318', fontSize: 12.5, marginBottom: 6 }}>{err}</div>}
      {facts.length === 0 && <div style={{ fontSize: 13, marginBottom: 8 }}>No facts saved yet.</div>}
      {KIND_ORDER.map((k) => {
        const rows = facts.filter((f) => f.kind === k);
        if (!rows.length) return null;
        return (
          <div key={k} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy, #00395C)', marginBottom: 2 }}>{KIND_LABELS[k]}</div>
            {rows.map((f) => <FactRow key={f.id} f={f} busy={busy} onRetire={retire} />)}
          </div>
        );
      })}
      <AddFact onSaved={(out) => { if (out && out.facts) setFacts(out.facts); else load(); }} />
    </div>
  );
}
